import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Receipt,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import { formatDate } from "../utils/dateUtils";

interface Bill {
  _id: string;
  billNumber: string;
  date?: Date;
  dueDate?: Date;
  status: string;
  total: number;
  amountDue?: number;
  amountPaid?: number;
  currencyCode?: string;
  reference?: string;
}

interface BillListModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierId: string;
  supplierName: string;
}

export const BillListModal: React.FC<BillListModalProps> = ({
  isOpen,
  onClose,
  supplierId,
  supplierName,
}) => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/api/bills?supplierId=${supplierId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = await response.json();
      if (data.success) {
        setBills(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch bills:", error);
    } finally {
      setLoading(false);
    }
  }, [supplierId, apiUrl]);

  useEffect(() => {
    if (isOpen && supplierId) {
      fetchBills();
    }
  }, [isOpen, supplierId, fetchBills]);

  const formatCurrency = (amount: number, currencyCode?: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
    }).format(amount);
  };

  const formatDateLocal = (date?: Date | string) => {
    if (!date) return "N/A";
    return formatDate(date);
  };

  const getStatusConfig = (status: string, amountDue?: number) => {
    if (status === "PAID" || (amountDue !== undefined && amountDue === 0)) {
      return {
        icon: CheckCircle,
        color: "text-success-600 dark:text-success-400",
        bg: "bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50",
        border: "border-green-200 dark:border-green-800",
        label: "Paid",
      };
    }
    if (status === "APPROVED" || status === "SUBMITTED") {
      return {
        icon: Clock,
        color: "text-primary-600 dark:text-primary-400",
        bg: "bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50",
        border: "border-primary-200 dark:border-primary-800",
        label: "Unpaid",
      };
    }
    if (status === "VOIDED" || status === "DELETED") {
      return {
        icon: X,
        color: "text-error-600 dark:text-error-400",
        bg: "bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50",
        border: "border-red-200 dark:border-red-800",
        label: status,
      };
    }
    return {
      icon: AlertCircle,
      color: "text-gray-600 dark:text-gray-400",
      bg: "bg-gray-50 dark:bg-gray-800/60",
      border: "border-gray-200 dark:border-gray-700",
      label: status,
    };
  };

  if (!isOpen) return null;

  const paidBills = bills.filter(
    (bill) =>
      bill.status === "PAID" ||
      (bill.amountDue !== undefined && bill.amountDue === 0),
  );
  const unpaidBills = bills.filter(
    (bill) =>
      bill.status !== "PAID" &&
      bill.status !== "VOIDED" &&
      bill.status !== "DELETED" &&
      (bill.amountDue === undefined || bill.amountDue > 0),
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-gray-700 dark:border dark:border-purple-800/50 rounded-lg flex items-center justify-center">
              <Receipt className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Bills
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {supplierName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="text-center">
            <div className="text-2xl font-bold text-success-600 dark:text-success-400">
              {paidBills.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Paid</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {unpaidBills.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Unpaid
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {bills.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Loading bills...
              </p>
            </div>
          ) : bills.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No bills found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bills.map((bill) => {
                const config = getStatusConfig(bill.status, bill.amountDue);
                const StatusIcon = config.icon;

                return (
                  <div
                    key={bill._id}
                    className={`border rounded-lg p-4 ${config.border} ${config.bg} hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <StatusIcon
                          className={`w-5 h-5 ${config.color} mt-0.5 flex-shrink-0`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {bill.billNumber}
                            </h3>
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded ${config.color}`}
                            >
                              {config.label}
                            </span>
                          </div>

                          {bill.reference && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                              Ref: {bill.reference}
                            </p>
                          )}

                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                              <Calendar className="w-3 h-3" />
                              <span>Date: {formatDateLocal(bill.date)}</span>
                            </div>
                            <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                              <Calendar className="w-3 h-3" />
                              <span>Due: {formatDateLocal(bill.dueDate)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="text-right ml-4">
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {formatCurrency(bill.total, bill.currencyCode)}
                        </div>
                        {bill.amountDue !== undefined && bill.amountDue > 0 && (
                          <div className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                            Due:{" "}
                            {formatCurrency(bill.amountDue, bill.currencyCode)}
                          </div>
                        )}
                        {bill.amountPaid !== undefined &&
                          bill.amountPaid > 0 && (
                            <div className="text-xs text-success-600 dark:text-success-400">
                              Paid:{" "}
                              {formatCurrency(
                                bill.amountPaid,
                                bill.currencyCode,
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
